import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { 
  User, 
  CompleteUser,
  Tournament, 
  Room, 
  Transaction, 
  Deposit, 
  Withdrawal, 
  DashboardStats,
  DashboardReport,
  ApiResponse,
  LoginResponse,
  PaginatedResponse,
  TransactionFilters,
  GetTransactionsResponse,
  DepositFilters,
  GetDepositsResponse,
  WithdrawalFilters,
  GetWithdrawalsResponse,
  UserFilters,
  GetUsersResponse,
  WalletUpdateRequest,
  UserGrowthData,
  TopPlayer,
  UserActivityData,
  ReferralStats,
  TournamentFilters,
  GetTournamentsResponse,
  TournamentForm,
  TournamentAnalytics,
  RoomFilters,
  GetRoomsResponse,
  RoomForm,
  RoomStatus,
  RoomStatistics,
  RoomPerformance,
  RoomHistoryResponse,
  WinningPatternFilters,
  GetWinningPatternsResponse,
  WinningPatternData,
  WinningPatternForm,
  GetSettingsResponse,
  SettingsData,
  SettingsForm,
  PaymentMethodData,
  PaymentMethodForm,
  PaymentMethodUpdateForm,
  GetPaymentMethodsResponse,
  PaymentMethodFilters,
  PaymentMethodReorderRequest,
  AdminData,
  AdminForm,
  AdminUpdateForm,
  GetAdminsResponse,
  AdminFilters,
  RequestOtpRequest,
  VerifyOtpRequest,
  VerifyOtpResponse,
  MediaData,
  MediaForm,
  MediaUpdateForm,
  MediaFilters,
  GetMediaResponse,
  MediaStatistics,
  GetMediaStatsResponse,
  AgentRequest,
  AgentRequestFilters,
  AgentRequestsResponse,
  ApproveAgentRequestResponse,
  RejectAgentRequestResponse,
  GiftBonusPreview
} from '../types';
import { getCurrentAdminInfo } from '../utils/adminUtils';

// API Configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.ibexgames.fun/api';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('admin_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for token refresh and error handling
    this.api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('admin_token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  // Authentication
  async staticLogin(code: string): Promise<LoginResponse> {
    console.log('API: Attempting static login with code:', code);
    try {
      const response = await this.api.post('/auth/static-login', { code });
      console.log('API: Static login response:', response.data);
      if (response.data.success) {
        localStorage.setItem('admin_token', response.data.token);
      }
      return response.data;
    } catch (error) {
      console.error('API: Static login error:', error);
      throw error;
    }
  }

  async verifyToken(token: string): Promise<ApiResponse<{ valid: boolean; user: any }>> {
    console.log('API: Verifying token:', token);
    try {
      const response = await this.api.post('/auth/verify-token', { token });
      console.log('API: Verify token response:', response.data);
      return response.data;
    } catch (error) {
      console.error('API: Verify token error:', error);
      // If verify-token endpoint doesn't exist, we'll assume the token is valid
      // and get admin data instead
      try {
        const adminResponse = await this.api.get('/users/admin/data');
        console.log('API: Admin data response:', adminResponse.data);
        return {
          success: true,
          data: {
            valid: true,
            user: adminResponse.data.data || adminResponse.data
          }
        };
      } catch (adminError) {
        console.error('API: Admin data error:', adminError);
        throw error;
      }
    }
  }

  async getAdminData(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/users/admin/data');
    return response.data;
  }

  // Dashboard
  async getDashboardReport(period: 'day' | 'week' | 'month' | 'all' = 'all'): Promise<DashboardReport> {
    const response = await this.api.get(`/dashboard/report?period=${period}`);
    return response.data;
  }

  async getDashboardStats(): Promise<ApiResponse<DashboardStats>> {
    const response = await this.api.get('/admin/dashboard/stats');
    return response.data;
  }

  async getRevenueAnalytics(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/admin/analytics/revenue');
    return response.data;
  }

  async getRecentActivity(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/admin/dashboard/recent-activity');
    return response.data;
  }

  // User Management
  async getUsers(page = 1, limit = 20, filters?: UserFilters): Promise<GetUsersResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (filters?.search) params.append('search', filters.search);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.hasDeposit !== undefined) params.append('hasDeposit', filters.hasDeposit.toString());
    if (filters?.minWallet) params.append('minWallet', filters.minWallet.toString());
    if (filters?.maxWallet) params.append('maxWallet', filters.maxWallet.toString());
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);
    if (filters?.dateRange) {
      params.append('startDate', filters.dateRange.start);
      params.append('endDate', filters.dateRange.end);
    }

    const response = await this.api.get(`/users?${params.toString()}`);
    return response.data;
  }

  async getUserDetails(userId: string): Promise<CompleteUser> {
    const response = await this.api.get(`/users/${userId}`);
    return response.data;
  }

  async updateUserWallet(data: {
    phoneNumber: string;
    amount: number;
  }): Promise<any> {
    const response = await this.api.post('/users/wallet/add-balance', data);
    return response.data;
  }

  async setUserWallet(data: {
    userId: string;
    balance: number;
    reason?: string;
  }): Promise<ApiResponse<any>> {
    const response = await this.api.post('/admin/user/wallet/set', data);
    return response.data;
  }

  async getUserTransactions(userId: string, page = 1, limit = 10): Promise<{ success: boolean; data: Transaction[]; pagination: { total: number; page: number; pages: number; limit: number } }> {
    const response = await this.api.get(`/transactions/user/${userId}?page=${page}&limit=${limit}`);
    return response.data;
  }

  async getUserInvites(userId: string): Promise<{ success: boolean; invites: any[] }> {
    const response = await this.api.get(`/invites/user/${userId}`);
    return response.data;
  }

  async blockUser(userId: string, reason?: string): Promise<CompleteUser> {
    const response = await this.api.post(`/users/${userId}/block`, { reason });
    return response.data;
  }

  async unblockUser(userId: string, reason?: string): Promise<CompleteUser> {
    const response = await this.api.post(`/users/${userId}/unblock`, { reason });
    return response.data;
  }

  async deleteUser(userId: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.api.delete(`/users/${userId}`);
    return response.data;
  }

  async resetUserPassword(userId: string): Promise<ApiResponse<{ success: boolean }>> {
    const response = await this.api.post(`/users/${userId}/reset-password`);
    return response.data;
  }

  async getTodayRegisteredUsers(): Promise<ApiResponse<{ count: number; users: CompleteUser[] }>> {
    const response = await this.api.get('/users/today/registered');
    return response.data;
  }

  // User Analytics
  async getUserGrowthData(): Promise<ApiResponse<UserGrowthData[]>> {
    const response = await this.api.get('/users/analytics/growth');
    return response.data;
  }

  async getTopPlayers(): Promise<ApiResponse<TopPlayer[]>> {
    const response = await this.api.get('/users/analytics/top-players');
    return response.data;
  }

  async getUserActivityData(): Promise<ApiResponse<UserActivityData[]>> {
    const response = await this.api.get('/users/analytics/activity');
    return response.data;
  }

  async getReferralStats(): Promise<ApiResponse<ReferralStats>> {
    const response = await this.api.get('/users/analytics/referrals');
    return response.data;
  }

  // Tournament Management
  async getTournaments(page = 1, limit = 20, filters?: TournamentFilters): Promise<GetTournamentsResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (filters) {
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (filters.minStake) params.append('minStake', filters.minStake.toString());
      if (filters.maxStake) params.append('maxStake', filters.maxStake.toString());
      if (filters.minPlayers) params.append('minPlayers', filters.minPlayers.toString());
      if (filters.maxPlayers) params.append('maxPlayers', filters.maxPlayers.toString());
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder.toString());
      if (filters.dateRange) {
        params.append('startDate', filters.dateRange.start);
        params.append('endDate', filters.dateRange.end);
      }
    }
    
    const response = await this.api.get(`/admin/tournaments/stats`);
    console.log('Tournaments API Response:', response.data);
    return response.data;
  }

  async getTournament(tournamentId: string): Promise<Tournament> {
    const response = await this.api.get(`/tournaments/${tournamentId}`);
    return response.data;
  }

  async createTournament(tournamentData: TournamentForm): Promise<Tournament> {
    // Create can take a bit for card generation; notifications run in background on API
    const response = await this.api.post('/admin/tournaments', tournamentData, {
      timeout: 60000,
    });
    return response.data?.tournament || response.data;
  }

  async updateTournament(tournamentId: string, tournamentData: Partial<TournamentForm>): Promise<Tournament> {
    const response = await this.api.put(`/admin/tournaments/${tournamentId}`, tournamentData);
    return response.data;
  }

  async deleteTournament(tournamentId: string): Promise<{ success: boolean }> {
    const response = await this.api.delete(`/admin/tournaments/${tournamentId}`);
    return response.data;
  }

  async forceDeleteTournament(tournamentId: string): Promise<{ success: boolean }> {
    const response = await this.api.delete(`/admin/tournaments/${tournamentId}`);
    return response.data;
  }

  async forceStartTournament(tournamentId: string): Promise<Tournament> {
    const response = await this.api.post(`/admin/tournaments/${tournamentId}/force-start`);
    return response.data;
  }

  async cancelTournament(tournamentId: string): Promise<Tournament> {
    const response = await this.api.post(`/admin/tournaments/${tournamentId}/cancel`);
    return response.data;
  }

  async getTournamentAnalytics(tournamentId: string): Promise<TournamentAnalytics> {
    const response = await this.api.get(`/admin/tournaments/${tournamentId}/analytics`);
    return response.data;
  }

  async getTournamentPlayers(tournamentId: string): Promise<CompleteUser[]> {
    const response = await this.api.get(`/tournaments/${tournamentId}/players`);
    return response.data;
  }

  async addPlayerToTournament(tournamentId: string, userId: string): Promise<Tournament> {
    const response = await this.api.post(`/tournaments/${tournamentId}/add-player`, { userId });
    return response.data;
  }

  async removePlayerFromTournament(tournamentId: string, userId: string): Promise<Tournament> {
    const response = await this.api.post(`/tournaments/${tournamentId}/remove-player`, { userId });
    return response.data;
  }

  async generateTournamentCards(tournamentId: string): Promise<{ success: boolean }> {
    const response = await this.api.post(`/tournaments/${tournamentId}/generate-cards`);
    return response.data;
  }

  // Room Management
  async getRooms(filters?: RoomFilters): Promise<Room[]> {
    const params = new URLSearchParams();
    
    if (filters) {
      if (filters.status) params.append('status', filters.status);
      if (filters.stake) params.append('stake', filters.stake.toString());
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder.toString());
    }
    
    const response = await this.api.get(`/rooms?${params.toString()}`);
    console.log('Rooms API Response:', response.data);
    return response.data;
  }

  async getRoom(roomId: string): Promise<Room> {
    const response = await this.api.get(`/rooms/${roomId}`);
    return response.data;
  }

  async createRoom(roomData: RoomForm): Promise<Room> {
    const response = await this.api.post('/rooms', roomData);
    return response.data;
  }

  async updateRoomStatus(roomId: string, status: RoomStatus, countdown?: number): Promise<{ success: boolean; message: string; room: Room }> {
    const response = await this.api.put(`/rooms/${roomId}/status`, { status, countdown });
    return response.data;
  }

  async forceStartRoom(roomId: string, reason?: string): Promise<{ success: boolean; message: string; room: Room }> {
    const response = await this.api.post(`/rooms/${roomId}/force-start`, { reason });
    return response.data;
  }

  async forceEndRoom(roomId: string, reason?: string): Promise<{ success: boolean; message: string; room: Room }> {
    const response = await this.api.post(`/rooms/${roomId}/force-end`, { reason });
    return response.data;
  }

  async deleteRoom(roomId: string, reason?: string): Promise<{ success: boolean; message: string; room: Room }> {
    const response = await this.api.delete(`/rooms/${roomId}`, { data: { reason } });
    return response.data;
  }

  async forceDeleteRoom(roomId: string): Promise<{ success: boolean; message: string; deletedItems: any }> {
    const response = await this.api.delete(`/rooms/${roomId}/force`);
    return response.data;
  }

  async getRoomPlayers(roomId: string): Promise<import('../types').RoomPlayersResponse> {
    const response = await this.api.get(`/rooms/${roomId}/players`);
    return response.data;
  }

  async getRoomStatistics(roomId: string): Promise<RoomStatistics> {
    const response = await this.api.get(`/rooms/${roomId}/statistics`);
    return response.data;
  }

  async getRoomPerformance(roomId: string): Promise<RoomPerformance> {
    const response = await this.api.get(`/rooms/${roomId}/performance`);
    return response.data;
  }

  async getRoomHistory(roomId: string, page = 1, limit = 10, startDate?: string, endDate?: string): Promise<RoomHistoryResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    
    const response = await this.api.get(`/rooms/${roomId}/history?${params.toString()}`);
    return response.data;
  }

  async addPlayerToRoom(roomId: string, userId: string): Promise<{ success: boolean; message: string; room: Room }> {
    const response = await this.api.post(`/rooms/${roomId}/add-player`, { userId });
    return response.data;
  }

  async removePlayerFromRoom(roomId: string, userId: string, reason?: string): Promise<{ success: boolean; message: string; room: Room }> {
    const response = await this.api.post(`/rooms/${roomId}/remove-player`, { userId, reason });
    return response.data;
  }

  async generateRoomCards(roomId: string, cardCount = 100): Promise<{ success: boolean; message: string; cardsGenerated: number }> {
    const response = await this.api.post(`/rooms/${roomId}/generate-cards`, { cardCount });
    return response.data;
  }

  // Transaction Management
  async getTransactions(page = 1, limit = 10, filters?: TransactionFilters): Promise<GetTransactionsResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (filters?.type) params.append('type', filters.type);
    if (filters?.username) params.append('username', filters.username);
    if (filters?.phoneNumber) params.append('phoneNumber', filters.phoneNumber);
    if (filters?.dateRange) {
      params.append('startDate', filters.dateRange.start);
      params.append('endDate', filters.dateRange.end);
    }
    if (filters?.minAmount) params.append('minAmount', filters.minAmount.toString());
    if (filters?.maxAmount) params.append('maxAmount', filters.maxAmount.toString());
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder.toString());

    const response = await this.api.get(`/transactions?${params.toString()}`);
    return response.data;
  }

  async getTransaction(transactionId: string): Promise<Transaction> {
    const response = await this.api.get(`/transactions/${transactionId}`);
    return response.data;
  }

  async getTransactionStats(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/transactions/stats');
    return response.data;
  }

  // Deposit Management
  async getDeposits(page = 1, limit = 10, filters?: DepositFilters): Promise<GetDepositsResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (filters?.status) params.append('status', filters.status);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.agentPhone) params.append('agentPhone', filters.agentPhone);
    if (filters?.phoneNumber) params.append('phoneNumber', filters.phoneNumber);
    if (filters?.bankType) params.append('bankType', filters.bankType);
    if (filters?.minAmount) params.append('minAmount', filters.minAmount.toString());
    if (filters?.maxAmount) params.append('maxAmount', filters.maxAmount.toString());
    if (filters?.reference) params.append('reference', filters.reference);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await this.api.get(`/deposits/all?${params.toString()}`);
    return response.data;
  }

  async getDeposit(depositId: string): Promise<Deposit> {
    const response = await this.api.get(`/deposits/${depositId}`);
    return response.data;
  }

  async completeDeposit(depositId: string, data: { bankTransaction: string; bankType: string }): Promise<{
    success: boolean;
    message: string;
    deposit: Deposit;
    user: {
      id: string;
      username: string;
      wallet: number;
    };
  }> {
    const response = await this.api.put(`/deposits/${depositId}/complete`, data);
    return response.data;
  }

  async verifyCryptoDeposit(depositId: string, data?: { cryptoTxId?: string }): Promise<{
    success: boolean;
    message: string;
    deposit?: Deposit;
    user?: {
      id: string;
      username: string;
      wallet: number;
    };
  }> {
    const response = await this.api.post(`/deposits/${depositId}/verify-crypto`, data || {});
    return response.data;
  }

  async failDeposit(depositId: string, reason?: string): Promise<ApiResponse<Deposit>> {
    const response = await this.api.post(`/deposits/${depositId}/fail`, {
      reason,
      notes: reason,
    });
    return response.data;
  }

  async getAgentStats(agentPhone: string): Promise<ApiResponse<any>> {
    const response = await this.api.get(`/deposits/agent/${agentPhone}/stats`);
    return response.data;
  }

  // Withdrawal Management
  async getWithdrawals(page = 1, limit = 10, filters?: WithdrawalFilters): Promise<GetWithdrawalsResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (filters?.status) params.append('status', filters.status);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.phoneNumber) params.append('phoneNumber', filters.phoneNumber);
    if (filters?.bankType) params.append('bankType', filters.bankType);
    if (filters?.minAmount) params.append('minAmount', filters.minAmount.toString());
    if (filters?.maxAmount) params.append('maxAmount', filters.maxAmount.toString());
    if (filters?.search) params.append('search', filters.search);
    if (filters?.sortBy) params.append('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await this.api.get(`/withdrawals/all?${params.toString()}`);
    return response.data;
  }

  async getWithdrawal(withdrawalId: string): Promise<Withdrawal> {
    const response = await this.api.get(`/withdrawals/${withdrawalId}`);
    return response.data;
  }

  async updateWithdrawalStatus(withdrawalId: string, status: string, adminNote?: string): Promise<{
    success: boolean;
    message: string;
    data: Withdrawal;
  }> {
    // Get current admin info
    const adminInfo = getCurrentAdminInfo();
    const adminId = adminInfo?.telegramId || adminInfo?.id || '';
    
    const response = await this.api.put(`/withdrawals/${withdrawalId}`, { 
      status, 
      adminNote,
      adminId 
    });
    
    // Handle the actual API response structure
    const apiResponse = response.data;
    
    // If the update was successful, fetch the updated withdrawal data
    if (apiResponse.message && apiResponse.withdrawalRequest) {
      try {
        const updatedWithdrawal = await this.getWithdrawal(withdrawalId);
        return {
          success: true,
          message: apiResponse.message,
          data: updatedWithdrawal
        };
      } catch (error) {
        // If fetching fails, return a success response with the basic info we have
        return {
          success: true,
          message: apiResponse.message,
          data: {
            id: apiResponse.withdrawalRequest.id || withdrawalId,
            status: apiResponse.withdrawalRequest.status || status,
            adminNote: apiResponse.withdrawalRequest.adminNote || adminNote || '',
            processedAt: apiResponse.withdrawalRequest.processedAt,
            user: { id: '', username: '', telegramUsername: '', telegramId: '' },
            amount: 0,
            bankType: '',
            phoneNumber: '',
            accountNumber: '',
            description: '',
            createdAt: '',
          } as Withdrawal
        };
      }
    }
    
    // Fallback for unexpected response structure
    return {
      success: false,
      message: 'Unexpected response from server',
      data: {} as Withdrawal
    };
  }

  // Analytics
  async getGameAnalytics(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/admin/analytics/games');
    return response.data;
  }

  async getUserAnalytics(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/admin/analytics/users');
    return response.data;
  }

  async getRevenueAnalyticsDetailed(): Promise<ApiResponse<any>> {
    const response = await this.api.get('/analytics/revenue/detailed');
    return response.data;
  }

  // Winning Pattern Management
  async getWinningPatterns(page = 1, limit = 20, filters?: WinningPatternFilters): Promise<GetWinningPatternsResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    if (filters) {
      if (filters.patternType) params.append('patternType', filters.patternType);
      if (filters.search) params.append('search', filters.search);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    }
    
    const response = await this.api.get(`/winning-patterns/all?${params.toString()}`);
    
    // Handle different response structures
    const data = response.data;
    if (data.success) {
      // If the response has a data array, return as is
      if (data.data) {
        return data;
      }
      // If the response has a patterns array, normalize it
      if (data.patterns) {
        return {
          success: true,
          data: data.patterns,
          pagination: data.pagination || {
            total: data.patterns.length,
            page: page,
            pages: 1,
            limit: limit
          }
        };
      }
      // If the response has a single pattern, wrap it in an array
      if (data.pattern) {
        return {
          success: true,
          data: [data.pattern],
          pagination: {
            total: 1,
            page: 1,
            pages: 1,
            limit: 1
          }
        };
      }
    }
    
    return data;
  }

  async getWinningPattern(patternId: string): Promise<WinningPatternData> {
    const response = await this.api.get(`/winning-patterns/${patternId}`);
    return response.data;
  }

  async createWinningPattern(patternData: WinningPatternForm): Promise<WinningPatternData> {
    const response = await this.api.post('/winning-patterns', patternData);
    return response.data;
  }

  async updateWinningPattern(patternId: string, patternData: Partial<WinningPatternForm>): Promise<WinningPatternData> {
    const response = await this.api.put(`/winning-patterns/${patternId}`, patternData);
    return response.data;
  }

  async deleteWinningPattern(patternId: string): Promise<{ success: boolean }> {
    const response = await this.api.delete(`/winning-patterns/${patternId}`);
    return response.data;
  }

  // Settings Management
  async getSettings(): Promise<GetSettingsResponse> {
    const response = await this.api.get('/settings');
    return response.data;
  }

  async updateSettings(settingsData: Partial<SettingsForm>): Promise<SettingsData> {
    const response = await this.api.put('/settings', settingsData);
    return response.data.settings ?? response.data;
  }

  async resetSettings(): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post('/settings/reset');
    return response.data;
  }

  // Payment Method Management
  async getPaymentMethods(page = 1, limit = 20, filters?: PaymentMethodFilters): Promise<GetPaymentMethodsResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (filters) {
      if (filters.type) params.append('type', filters.type);
      if (filters.search) params.append('search', filters.search);
      if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    }
    
    const response = await this.api.get(`/payment-methods/all?${params.toString()}`);
    
    // Handle different response structures
    const data = response.data;
    
    // If the response has a paymentMethods array, normalize it
    if (data.paymentMethods) {
      return {
        success: true,
        data: data.paymentMethods,
        pagination: data.pagination || {
          total: data.paymentMethods.length,
          page: page,
          pages: 1,
          limit: limit
        }
      };
    }
    
    // If the response has a data array, return as is
    if (data.data) {
      return data;
    }
    
    // If the response has a methods array, normalize it
    if (data.methods) {
      return {
        success: true,
        data: data.methods,
        pagination: data.pagination || {
          total: data.methods.length,
          page: page,
          pages: 1,
          limit: limit
        }
      };
    }
    
    // If the response has a single method, wrap it in an array
    if (data.method) {
      return {
        success: true,
        data: [data.method],
        pagination: {
          total: 1,
          page: 1,
          pages: 1,
          limit: 1
        }
      };
    }
    
    return data;
  }

  async getPaymentMethod(methodId: string): Promise<PaymentMethodData> {
    const response = await this.api.get(`/payment-methods/admin/${methodId}`);
    return response.data;
  }

  async createPaymentMethod(methodData: PaymentMethodForm): Promise<PaymentMethodData> {
    const response = await this.api.post('/payment-methods/admin/create', methodData);
    return response.data.paymentMethod || response.data;
  }

  async updatePaymentMethod(methodId: string, methodData: PaymentMethodUpdateForm): Promise<PaymentMethodData> {
    const response = await this.api.put(`/payment-methods/admin/${methodId}`, methodData);
    return response.data.paymentMethod || response.data;
  }

  async togglePaymentMethodStatus(methodId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.patch(`/payment-methods/admin/${methodId}/toggle-status`);
    return response.data;
  }

  async deletePaymentMethod(methodId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete(`/payment-methods/admin/${methodId}`);
    return response.data;
  }

  async reorderPaymentMethods(reorderData: PaymentMethodReorderRequest): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post('/payment-methods/admin/reorder', reorderData);
    return response.data;
  }

  // Admin Management
  async requestOtp(requestData: RequestOtpRequest): Promise<{ success: boolean; message: string }> {
    const response = await this.api.post('/admin/request-otp', requestData);
    const data = response.data;
    
    // Handle response format: {"message":"OTP sent successfully to your email","email":"haftomg96@gmail.com"}
    if (data.message && data.message.includes('successfully')) {
      return {
        success: true,
        message: data.message
      };
    }
    
    // Handle response format with success property
    if (data.success !== undefined) {
      return data;
    }
    
    // Default fallback
    return {
      success: true,
      message: data.message || 'OTP sent successfully'
    };
  }

  async verifyOtp(verifyData: VerifyOtpRequest): Promise<VerifyOtpResponse> {
    const response = await this.api.post('/admin/verify-otp', verifyData);
    return response.data;
  }

  async getAdminProfile(): Promise<AdminData> {
    const response = await this.api.get('/admin/profile');
    return response.data?.admin || response.data;
  }

  async getAdmins(page = 1, limit = 20, filters?: AdminFilters): Promise<GetAdminsResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (filters) {
      if (filters.role) params.append('role', filters.role);
      if (filters.search) params.append('search', filters.search);
      if (filters.isActive !== undefined) params.append('isActive', filters.isActive.toString());
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    }
    
    const response = await this.api.get(`/admin/all?${params.toString()}`);
    
    // Handle different response structures
    const data = response.data;
    
    // If the response has an admins array, normalize it
    if (data.admins) {
      return {
        success: true,
        data: data.admins,
        pagination: data.pagination || {
          total: data.admins.length,
          page: page,
          pages: 1,
          limit: limit
        }
      };
    }
    
    // If the response has a data array, return as is
    if (data.data) {
      return data;
    }
    
    // If the response has a single admin, wrap it in an array
    if (data.admin) {
      return {
        success: true,
        data: [data.admin],
        pagination: {
          total: 1,
          page: 1,
          pages: 1,
          limit: 1
        }
      };
    }
    
    return data;
  }

  async createAdmin(adminData: AdminForm): Promise<AdminData> {
    const response = await this.api.post('/admin/create', adminData);
    const data = response.data;
    // API returns { message, admin }; normalize to AdminData
    return (data?.admin ?? data) as AdminData;
  }

  async updateAdmin(adminId: string, adminData: AdminUpdateForm): Promise<AdminData> {
    const response = await this.api.put(`/admin/${adminId}`, adminData);
    return response.data;
  }

  async toggleAdminStatus(adminId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.patch(`/admin/${adminId}/toggle-status`);
    return response.data;
  }

  async deleteAdmin(adminId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete(`/admin/${adminId}`);
    return response.data;
  }

  // Media Management
  async getMedia(page = 1, limit = 20, filters?: MediaFilters): Promise<GetMediaResponse> {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (filters) {
      if (filters.type) params.append('type', filters.type);
      if (filters.isVideo !== undefined) params.append('isVideo', filters.isVideo.toString());
      if (filters.search) params.append('search', filters.search);
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
    }
    
    const response = await this.api.get(`/media?${params.toString()}`);
    return response.data;
  }

  async getMediaItem(mediaId: string): Promise<MediaData> {
    const response = await this.api.get(`/media/${mediaId}`);
    return response.data.media;
  }

  async createMedia(mediaData: MediaForm): Promise<{
    success: boolean;
    message: string;
    media: MediaData;
  }> {
    const formData = new FormData();
    formData.append('file', mediaData.file);
    formData.append('isVideo', mediaData.isVideo.toString());
    formData.append('type', mediaData.type);
    
    const response = await this.api.post('/media', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async updateMedia(mediaId: string, mediaData: MediaUpdateForm): Promise<{
    success: boolean;
    message: string;
    media: MediaData;
  }> {
    const response = await this.api.put(`/media/${mediaId}`, mediaData);
    return response.data;
  }

  async deleteMedia(mediaId: string): Promise<{ success: boolean; message: string }> {
    const response = await this.api.delete(`/media/${mediaId}`);
    return response.data;
  }

  async getMediaStats(): Promise<GetMediaStatsResponse> {
    const response = await this.api.get('/media/stats');
    return response.data;
  }

  // Push Notifications
  async subscribeToPushNotifications(userId: string, subscription: any): Promise<ApiResponse<any>> {
    const response = await this.api.post('/push/subscribe', {
      userId,
      subscription
    });
    return response.data;
  }

  async unsubscribeFromPushNotifications(endpoint: string): Promise<ApiResponse<any>> {
    const response = await this.api.post('/push/unsubscribe', {
      endpoint
    });
    return response.data;
  }

  async getVapidPublicKey(): Promise<{ publicKey: string }> {
    const response = await this.api.get('/push/vapid-public-key');
    return response.data;
  }

  // Agent Requests
  async getAgentRequests(filters?: AgentRequestFilters): Promise<AgentRequestsResponse> {
    const params = new URLSearchParams();
    
    if (filters?.status) params.append('status', filters.status);
    if (filters?.city) params.append('city', filters.city);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const queryString = params.toString();
    const url = `/agent-requests/all${queryString ? `?${queryString}` : ''}`;
    
    const response = await this.api.get(url);
    return response.data;
  }

  async approveAgentRequest(requestId: string, adminNote?: string): Promise<ApproveAgentRequestResponse> {
    const response = await this.api.post(`/agent-requests/${requestId}/approve`, {
      adminNote: adminNote || undefined,
    });
    return response.data;
  }

  async rejectAgentRequest(requestId: string, adminNote: string): Promise<RejectAgentRequestResponse> {
    const response = await this.api.post(`/agent-requests/${requestId}/reject`, {
      adminNote,
    });
    return response.data;
  }

  async previewGiftBonus(amount = 10): Promise<ApiResponse<GiftBonusPreview>> {
    const response = await this.api.get(`/users/admin/gift-bonus/preview?amount=${amount}`);
    return response.data;
  }

  async giftBonusToAllPlayers(data: {
    amount: number;
    confirm: boolean;
    notifyTelegram?: boolean;
    notifyPush?: boolean;
  }): Promise<ApiResponse<{ batchId: string; amount: number; usersCredited: number; totalCost: number }>> {
    const response = await this.api.post('/users/admin/gift-bonus', data, {
      timeout: 120000,
    });
    return response.data;
  }

  async resendGiftTelegram(campaignId?: string): Promise<ApiResponse<{
    campaignId: string;
    batchId: string;
    amount: number;
    usersCredited: number;
  }>> {
    const response = await this.api.post(
      '/users/admin/gift-bonus/resend-telegram',
      campaignId ? { campaignId } : {},
      { timeout: 60000 }
    );
    return response.data;
  }

  // Flash Deposit Bonus Promo
  async getFlashDepositStatus(): Promise<{ success: boolean; active: boolean; percent?: number; remainingSeconds?: number; durationMinutes?: number; totalDepositsCredited?: number; totalBonusGiven?: number }> {
    const response = await this.api.get('/promos/flash-deposit/status');
    return response.data;
  }

  async startFlashDepositPromo(percent: number, durationMinutes: number): Promise<{ success: boolean; promo?: any; message?: string }> {
    const response = await this.api.post('/promos/flash-deposit/start', { percent, durationMinutes });
    return response.data;
  }

  async cancelFlashDepositPromo(): Promise<{ success: boolean; message?: string }> {
    const response = await this.api.post('/promos/flash-deposit/cancel');
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService; 